import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    LOCALE_ID,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { ReportType } from '@modeso/aumico-lib-core-fe';
import { WorkflowConfigurationSelectionEnum } from '@modeso/types__aumico-lib-annualreport-be';
import { ICompanyNameIdLegalFormDTO } from '@modeso/types__aumico-lib-companies-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { isValid } from 'date-fns';
import moment, { Moment } from 'moment';
import { distinctUntilChanged } from 'rxjs';

@UntilDestroy()
@Component({
    selector: 'new-project-wizard',
    templateUrl: 'new-project-wizard.component.html',
    styleUrls: ['new-project-wizard.component.scss'],
})
export class NewProjectWizardComponent implements OnInit, OnChanges {
    @Input() company?: ICompanyNameIdLegalFormDTO | undefined;
    @Input() balanceSheetDate?: Date | undefined;
    @Input() companiesList: ICompanyNameIdLegalFormDTO[] = [];

    @Output() selectedCompany = new EventEmitter<ICompanyNameIdLegalFormDTO>();
    @Output() selectedBalanceSheetDate = new EventEmitter<Date>();
    @Output() selectedReportType = new EventEmitter<ReportType>();
    @Output() closeButtonClicked = new EventEmitter<boolean>();

    @ViewChild('searchUserInput') searchUserInput!: ElementRef<HTMLInputElement>;
    @ViewChild('dateButtonWrapper') dateButtonWrapper!: ElementRef<HTMLInputElement>;

    public InvalidDateState = false;
    public enableKeyboardShortcuts: boolean = true;
    public pageNumber = 1;
    public dateFlyoutOpened = false;
    public flyoutPosition: { top: number; left: number; right: number } = { top: 0, left: 0, right: 0 };

    public form: FormGroup;
    public workflowConfigurationSelectionEnum = WorkflowConfigurationSelectionEnum;

    public totalNumberOfPages = 2;

    private _balanceSheetDateInput: string = '';
    public previousYear: Date;
    public currentYear: Date;
    public calendarSelectedDate: Moment | null = null;

    public reportTypes = [
        { id: 'annual', name: 'Annual' },
        { id: 'quarterly', name: 'Quarterly' },
        { id: 'monthly', name: 'Monthly' },
        { id: 'custom', name: 'Custom' },
    ];

    isCpmmActive: boolean = false;
    @Input() set cpmmIsActive(value: boolean) {
        this.isCpmmActive = value;
        this.totalNumberOfPages = 3;
    }

    // Enter -> Next/Submit (if current page is valid), Escape -> Cancel
    @HostListener('document:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        if (!this.enableKeyboardShortcuts) {
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            
            // Page 1: Company selection (always shown)
            if (this.pageNumber === 1 && this.form.get('company')?.valid) {
                this.next();
            }
            // Page 2: Report type selection (only shown when CPMM is active)
            else if (this.pageNumber === 2 && this.isCpmmActive && this.form.get('reportType')?.valid) {
                this.next();
            }
            // Page 3: Balance sheet date (always the final page)
            else if (this.pageNumber === 3 && this.form.get('balanceSheetDate')?.valid) {
                this.submit();
            }
        } else if (event.key === 'Escape') {
            event.preventDefault();
            if (this.dateFlyoutOpened) {
                this.dateFlyoutOpened = false;
                // Do not call onCancel if flyout was open; just close the flyout
                return;
            }
            this.onCancel();
        }
    }

    constructor(private fb: FormBuilder, @Inject(LOCALE_ID) private locale: string, private i18next: I18NextService) {
        this.reportTypes = [
            { id: 'annual', name: this.i18next.t('aumico_create_project_dialog_select_report_type_annual') as string },
            {
                id: 'quarterly',
                name: this.i18next.t('aumico_create_project_dialog_select_report_type_quarterly') as string,
            },
            {
                id: 'monthly',
                name: this.i18next.t('aumico_create_project_dialog_select_report_type_monthly') as string,
            },
            { id: 'custom', name: this.i18next.t('aumico_create_project_dialog_select_report_type_custom') as string },
        ];
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            if (this.searchUserInput && this.searchUserInput.nativeElement) {
                this.searchUserInput.nativeElement.focus();
            }
        });
    }
    flagFocusOnDateInput: boolean = false;
    ngAfterViewChecked(): void {
        if (this.pageNumber === 3 && this.flagFocusOnDateInput) {
            if (this.dateButtonWrapper && this.dateButtonWrapper.nativeElement) {
                this.dateButtonWrapper.nativeElement.focus();
            }
            this.flagFocusOnDateInput = false;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.balanceSheetDate != null && this.form != null) {
            this.form.get('balanceSheetDate').setValue(this.balanceSheetDate);
            this._balanceSheetDateInput = this.formatDate(this.balanceSheetDate);
            this.calendarSelectedDate = moment.utc(this.balanceSheetDate).startOf('day');
        }

        if (changes['company']?.currentValue != null) {
            // if company is selected skip company selection
            if (this.isCpmmActive) {
                this.pageNumber = 2;
            } else {
                // skip report type selection if cpmm is not active
                this.pageNumber = 3;
                this.selectedReportType.emit(ReportType.ANNUAL);
            }
        }
    }

    ngOnInit(): void {
        const now = new Date();
        const prevYear = now.getFullYear() - 1;
        const currYear = now.getFullYear();

        this.previousYear = new Date(Date.UTC(prevYear, 11, 31, 0, 0, 0, 0)); // Dec 31st, previous year, UTC
        this.currentYear = new Date(Date.UTC(currYear, 11, 31, 0, 0, 0, 0));  // Dec 31st, this year, UTC

        this.initializeForm();

        this.form
            .get('company')
            .valueChanges.pipe(distinctUntilChanged(), untilDestroyed(this))
            .subscribe((value) => {
                this.selectedCompany.emit(value);
            });

        this.form
            .get('balanceSheetDate')
            .valueChanges.pipe(distinctUntilChanged(), untilDestroyed(this))
            .subscribe((value) => {

                this.selectedBalanceSheetDate.emit(value);
            });

        this.form
            .get('reportType')
            .valueChanges.pipe(distinctUntilChanged(), untilDestroyed(this))
            .subscribe((value) => {
                this.selectedReportType.emit(value.id);
            });
    }

    private initializeForm(): void {
        this.form = this.fb.group({
            company: [null, [Validators.required, this.notEmptyValidator]],
            reportType: [null, [Validators.required, this.notEmptyValidator]],
            balanceSheetDate: [null, [Validators.required, this.notEmptyValidator]],
        });

        if (!this.isCpmmActive) {
            this.form.get('reportType').setValue(this.reportTypes[0]);
        }

        if (this.company != null) {
            this.form.get('company').setValue(this.company);
            this.selectedCompany.emit(this.company);
        }

        if (this.balanceSheetDate != null) {
            this.form.get('balanceSheetDate').setValue(this.balanceSheetDate);
            this._balanceSheetDateInput = this.formatDate(this.balanceSheetDate);

            this.calendarSelectedDate = moment.utc(this.balanceSheetDate).startOf('day');
        }
    }

    private notEmptyValidator(control: UntypedFormControl): ValidationErrors | null {
        if (control.value == null || control.value.toString().trim().length === 0) {
            return { emptyField: true };
        }
        return null;
    }

    public goBack(): void {
        if (!this.isCpmmActive && this.pageNumber === 3) {
            // If cpmm is not active, skip report type selection when going back
            this.pageNumber = 1;
        } else {
            this.pageNumber--;
        }
    }

    public next(): void {
        if (!this.isCpmmActive && this.pageNumber === 1) {
            // If cpmm is not active, skip report type selection
            this.pageNumber = 3;
            this.selectedReportType.emit(ReportType.ANNUAL);

        } else {
            this.pageNumber++;
        }

        if (this.pageNumber === 3) {
            this.flagFocusOnDateInput = true;
        }
    }

    public parseDate(): void {
        const isEnglish = this.locale.startsWith('en');
        // Normalize separators: replace ., -, space with / (en) or . (others)
        let normalizedInput = this._balanceSheetDateInput.trim();
        if (isEnglish) {
            normalizedInput = normalizedInput.replace(/[.\-\s]/g, '/');
        } else {
            normalizedInput = normalizedInput.replace(/[\/\-\s]/g, '.');
        }

        // Parse manually to avoid timezone issues
        const parts = normalizedInput.split(isEnglish ? '/' : '.');
        let year: number, month: number, day: number;

        if (parts.length !== 3) {
            this.InvalidDateState = true;
            return;
        }

        if (isEnglish) {
            // MM/dd/yyyy format
            month = parseInt(parts[0], 10);
            day = parseInt(parts[1], 10);
            year = parseInt(parts[2], 10);
        } else {
            // dd.MM.yyyy format
            day = parseInt(parts[0], 10);
            month = parseInt(parts[1], 10);
            year = parseInt(parts[2], 10);
        }

        // Validate parsed values
        if (isNaN(year) || isNaN(month) || isNaN(day) ||
            year < 1900 || year > 2100 ||
            month < 1 || month > 12 ||
            day < 1 || day > 31) {
            this.InvalidDateState = true;
            return;
        }

        // Create timezone-free date at midnight UTC
        const parsed = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0)); // month is 0-based

        this.InvalidDateState = false;
        this.form.get('balanceSheetDate').setValue(parsed);
        this._balanceSheetDateInput = this.formatDate(parsed);
        this.calendarSelectedDate = moment.utc(parsed).startOf('day');
    }

    public submit(): void {
        if (this.pageNumber === 3) {
            this.parseDate();
            if (this.InvalidDateState) {
                return;
            }
        }
        this.closeButtonClicked.emit(true);
    }

    public onCancel(): void {
        this.closeButtonClicked.emit(false);
    }

    public closeDateFlyout(): void {
        this.dateFlyoutOpened = false;
    }

    public clearDate(): void {
        this.form.get('balanceSheetDate').setValue(null);
        this._balanceSheetDateInput = '';
        this.calendarSelectedDate = null;
    }

    get balanceSheetDateInput(): string {
        const date: Date = this.form?.get('balanceSheetDate')?.value;
        if (date instanceof Date && isValid(date)) {
            return this.formatDate(date);
        }
        return this._balanceSheetDateInput;
    }

    set balanceSheetDateInput(value: string) {
        this._balanceSheetDateInput = value;
    }

    public updateDate(m: Moment | Date): void {
        // Build UTC midnight for that calendar day
        let year: number, month: number, dday: number;

        if (m instanceof Date) {
            year = m.getFullYear();
            month = m.getMonth();
            dday = m.getDate();
        } else {
            year = m.year();
            month = m.month(); // 0-based
            dday = m.date();
        }

        const asUtc = new Date(Date.UTC(year, month, dday, 0, 0, 0, 0));
        this.form.get('balanceSheetDate').setValue(asUtc);
        this._balanceSheetDateInput = this.formatDate(asUtc);

        this.calendarSelectedDate = moment.utc(asUtc).startOf('day');
        this.dateFlyoutOpened = false;
    }

    public selectToday(): void {
        const today = new Date();
        const normalized = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 0, 0, 0, 0));
        this.form.get('balanceSheetDate').setValue(normalized);
        this._balanceSheetDateInput = this.formatDate(normalized);
        this.calendarSelectedDate = moment.utc(normalized).startOf('day');
        this.dateFlyoutOpened = false;
    }

    public select3112minus1year(): void {
        const normalized = new Date(Date.UTC(this.previousYear.getUTCFullYear(), this.previousYear.getUTCMonth(), this.previousYear.getUTCDate(), 0, 0, 0, 0));
        this.form.get('balanceSheetDate').setValue(normalized);
        this._balanceSheetDateInput = this.formatDate(normalized);
        this.calendarSelectedDate = moment.utc(normalized).startOf('day');
        this.dateFlyoutOpened = false;
    }

    public select3112thisYear(): void {
        const normalized = new Date(Date.UTC(this.currentYear.getUTCFullYear(), this.currentYear.getUTCMonth(), this.currentYear.getUTCDate(), 0, 0, 0, 0));
        this.form.get('balanceSheetDate').setValue(normalized);
        this._balanceSheetDateInput = this.formatDate(normalized);
        this.calendarSelectedDate = moment.utc(normalized).startOf('day');
        this.dateFlyoutOpened = false;
    }

    public openDateFlyout(buttonComponent: HTMLInputElement): void {
        this.dateFlyoutOpened = true;

        const rect = buttonComponent.getBoundingClientRect();

        const right = window.innerWidth - rect.left - 300;
        const top = rect.bottom;
        const left = rect.left;

        this.flyoutPosition = { top: top, right: left, left: right };
    }

    private formatDate(date: Date): string {

        // Extract year, month, day directly to avoid timezone issues
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth() + 1; // getUTCMonth() returns 0-11, so add 1
        const day = date.getUTCDate();

        // Format manually based on locale without timezone conversion
        const isEnglish = this.locale.startsWith('en');

        if (isEnglish) {
            // MM/dd/yyyy format for English
            return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
        } else {
            // dd.MM.yyyy format for others
            return `${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.${year}`;
        }
    }
}
